<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import settingsKeys from '../../../settings/settingsKeys.js';
    import { settingsGroupKeys } from '../pluginConfig.js';
    import Details from './generic/Details.svelte';
    import CheckMark from '../controls/generic/CheckMark.svelte';
    import FontFamilySetting from '../controls/FontFamilySetting.svelte';
    import FontSizeSetting from '../controls/FontSizeSetting.svelte';
    import LineHeightSetting from '../controls/LineHeightSetting.svelte';
    import LetterSpacingSetting from '../controls/LetterSpacingSetting.svelte';
    import WordSpacingSetting from '../controls/WordSpacingSetting.svelte';
    import LetterAndWordSpacingSetting from '../controls/LetterAndWordSpacingSetting.svelte';
    import { getIsNonDefaultState } from '../util.js';
    import { Icon } from '@oat-sa-private/ui-elements';
    import { __ } from '@oat-sa-private/ui-core';

    /**
     * Component of an a11y menu panel group
     */

    const groupKey = settingsGroupKeys.text;

    /**
     * @typedef {Object} Props
     * @property {Object} areaBroker
     * @property {Object} pluginConfig
     * @property {Object} initialSettingsState
     * @property {Function} onChange
     * @property {Function} onToggle
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        areaBroker,
        pluginConfig,
        initialSettingsState,
        onChange,
        onToggle
    } = $props();

    const nonDefaultsBySettingsKey = $state(
        [
            settingsKeys.fontFamily,
            settingsKeys.fontSize,
            settingsKeys.lineHeight,
            settingsKeys.letterSpacing,
            settingsKeys.wordSpacing,
            settingsKeys.letterAndWordSpacing
        ].reduce((acc, key) => {
            acc[key] = getIsNonDefaultState(key, initialSettingsState, pluginConfig);
            return acc;
        }, {})
    );

    let groupNonDefault = $derived(Object.values(nonDefaultsBySettingsKey).some(val => val));

    /**
     * Read and propagate change event
     * @param {Object} [newState]
     */
    function handleChange(newState = {}) {
        if (newState.key in nonDefaultsBySettingsKey) {
            nonDefaultsBySettingsKey[newState.key] = newState.state?.nonDefault;
        }
        onChange?.(newState);
    }
</script>

<Details
    key={groupKey}
    config={pluginConfig[groupKey]}
    collapsed={initialSettingsState[groupKey]?.collapsed}
    outlined={groupNonDefault}
    ariaLabelCollapse={__('collapse text settings')}
    ariaLabelExpand={__('expand text settings')}
    {onToggle}>
    {#snippet summary()}
        <h3 class="ui-heading">
            <Icon name="text-16" ariaHidden />
            {__('Text')}
            {#if groupNonDefault}
                <CheckMark />
            {/if}
        </h3>
    {/snippet}
    <ul>
        {#if pluginConfig[settingsKeys.fontFamily]?.enabled}
            <li>
                <FontFamilySetting
                    {areaBroker}
                    config={pluginConfig.fontFamily}
                    initialState={initialSettingsState[settingsKeys.fontFamily]?.toolState}
                    onChange={handleChange} />
            </li>
        {/if}
        {#if pluginConfig[settingsKeys.fontSize]?.enabled}
            <li>
                <FontSizeSetting
                    {areaBroker}
                    initialState={initialSettingsState[settingsKeys.fontSize]?.toolState}
                    onChange={handleChange} />
            </li>
        {/if}
        {#if pluginConfig[settingsKeys.lineHeight]?.enabled}
            <li>
                <LineHeightSetting
                    {areaBroker}
                    initialState={initialSettingsState[settingsKeys.lineHeight]?.toolState}
                    onChange={handleChange} />
            </li>
        {/if}
        {#if pluginConfig[settingsKeys.letterSpacing]?.enabled}
            <li>
                <LetterSpacingSetting
                    {areaBroker}
                    initialState={initialSettingsState[settingsKeys.letterSpacing]?.toolState}
                    onChange={handleChange} />
            </li>
        {/if}
        {#if pluginConfig[settingsKeys.wordSpacing]?.enabled}
            <li>
                <WordSpacingSetting
                    {areaBroker}
                    initialState={initialSettingsState[settingsKeys.wordSpacing]?.toolState}
                    onChange={handleChange} />
            </li>
        {/if}
        {#if pluginConfig[settingsKeys.letterAndWordSpacing]?.enabled}
            <li>
                <LetterAndWordSpacingSetting
                    {areaBroker}
                    initialState={initialSettingsState[settingsKeys.letterAndWordSpacing]?.toolState}
                    onChange={handleChange} />
            </li>
        {/if}
    </ul>
</Details>
